import { ethers } from 'ethers'
import { MULTICALL3 } from 'utils/swap/addresses'
import { MULTICALL3_ABI } from 'utils/swap/abi'

/**
 * What a B20 issuer can do to a wallet, read from the chain rather than from a
 * prospectus.
 *
 * A tokenized share is an ERC-20 with a supervisor. Every transfer asks the
 * Policy Registry precompile whether the sender, the receiver and the executor
 * are authorised; a blocked holder can have their balance burned outright by
 * `burnBlocked`; and the whole token can be paused, which stops every pool at
 * once. None of that is visible in a token list, a wallet or a DEX screener —
 * so this module reads it, and the page says what it found.
 *
 * The honest framing matters as much as the numbers. On 2026-09-11 every scope
 * on every one of these tokens pointed at policy 5, and policy 5 authorises an
 * unrelated address as readily as a pool — it is a **blocklist**: permitted
 * until named. That is very different from "you must be approved to hold this",
 * and only a read settles which one it is.
 */

/** Base's Policy Registry precompile. */
export const POLICY_REGISTRY = '0x8453000000000000000000000000000000000002'

const B20_ABI = [
  'function policyId(bytes32 policyScope) view returns (uint64)',
  'function isPaused(uint8 feature) view returns (bool)',
  'function TRANSFER_SENDER_POLICY() view returns (bytes32)',
  'function TRANSFER_RECEIVER_POLICY() view returns (bytes32)',
  'function TRANSFER_EXECUTOR_POLICY() view returns (bytes32)',
  'function MINT_RECEIVER_POLICY() view returns (bytes32)'
] as const

const REGISTRY_ABI = [
  'function isAuthorized(uint64 policyId, address account) view returns (bool)',
  'function policyAdmin(uint64 policyId) view returns (address)'
] as const

const b20 = new ethers.utils.Interface(B20_ABI as unknown as string[])
const registry = new ethers.utils.Interface(REGISTRY_ABI as unknown as string[])
const multicall = new ethers.utils.Interface(MULTICALL3_ABI as unknown as string[])

export type PolicyScopeName = 'sender' | 'receiver' | 'executor' | 'mint'

const SCOPE_GETTER: Record<PolicyScopeName, string> = {
  sender: 'TRANSFER_SENDER_POLICY',
  receiver: 'TRANSFER_RECEIVER_POLICY',
  executor: 'TRANSFER_EXECUTOR_POLICY',
  mint: 'MINT_RECEIVER_POLICY'
}

export const SCOPE_ORDER: PolicyScopeName[] = ['sender', 'receiver', 'executor', 'mint']

export const SCOPE_MEANING: Record<PolicyScopeName, string> = {
  sender: 'Whether a wallet may send — a blocked sender cannot sell or move the token.',
  receiver: 'Whether a wallet may be paid — a blocked receiver cannot be delivered to.',
  executor: 'Whether a contract may move the token on a holder’s behalf.',
  mint: 'Whether a wallet may receive newly issued tokens.'
}

/** The three pauses that exist on the live deployment. */
export const PAUSE_FEATURES = ['transfer', 'mint', 'burn'] as const
export type PauseFeature = (typeof PAUSE_FEATURES)[number]

export interface StockPolicy {
  /** Policy id per scope; 0 means no policy is bound and everything is allowed. */
  scopes: Record<PolicyScopeName, bigint | null>
  /** The address that may change who is on the list, for the sender scope. */
  admin: string | null
  paused: Record<PauseFeature, boolean | null>
  /** Per scope, whether `account` is currently authorised. Empty without one. */
  account: Partial<Record<PolicyScopeName, boolean>>
}

type Row = { success: boolean; returnData: string }

function decodeUint64(row: Row | undefined): bigint | null {
  if (!row || !row.success) return null
  try {
    return ethers.utils.defaultAbiCoder.decode(['uint64'], row.returnData)[0].toBigInt() as bigint
  } catch {
    return null
  }
}

function decodeBool(row: Row | undefined): boolean | null {
  if (!row || !row.success) return null
  try {
    return Boolean(ethers.utils.defaultAbiCoder.decode(['bool'], row.returnData)[0])
  } catch {
    return null
  }
}

function decodeBytes32(row: Row | undefined): string | null {
  if (!row || !row.success) return null
  try {
    return String(ethers.utils.defaultAbiCoder.decode(['bytes32'], row.returnData)[0])
  } catch {
    return null
  }
}

function decodeAddress(row: Row | undefined): string | null {
  if (!row || !row.success) return null
  try {
    const value = String(ethers.utils.defaultAbiCoder.decode(['address'], row.returnData)[0]).toLowerCase()
    return value === ethers.constants.AddressZero ? null : value
  } catch {
    return null
  }
}

/**
 * Reads a token's policy state in three round trips: the scope constants, the
 * ids bound to them, then what those ids say about an account.
 *
 * Three rather than one because each answer names the next question — there is
 * no way to ask "is this wallet allowed to sell" without first learning which
 * policy the token's sender scope points at. Every row may fail: the newer
 * seize scopes are not activated on this deployment and revert, which is
 * information rather than an error.
 */
export async function readStockPolicy(params: {
  provider: ethers.providers.Provider
  token: string
  account?: string | null
}): Promise<StockPolicy> {
  const { provider, token, account } = params
  const contract = new ethers.Contract(MULTICALL3, multicall, provider)
  const call = async (calls: Array<{ target: string; allowFailure: true; callData: string }>) =>
    (await contract.callStatic.aggregate3(calls)) as Row[]

  const scopeRows = await call(
    SCOPE_ORDER.map((scope) => ({
      target: token,
      allowFailure: true as const,
      callData: b20.encodeFunctionData(SCOPE_GETTER[scope], [])
    }))
  )
  const scopeKeys = SCOPE_ORDER.map((scope, i) => ({ scope, key: decodeBytes32(scopeRows[i]) }))

  const idCalls = scopeKeys
    .filter((s) => s.key !== null)
    .map((s) => ({
      target: token,
      allowFailure: true as const,
      callData: b20.encodeFunctionData('policyId', [s.key as string])
    }))
  const pauseCalls = PAUSE_FEATURES.map((_, index) => ({
    target: token,
    allowFailure: true as const,
    callData: b20.encodeFunctionData('isPaused', [index])
  }))
  const second = await call([...idCalls, ...pauseCalls])

  const scopes = {} as Record<PolicyScopeName, bigint | null>
  for (const scope of SCOPE_ORDER) scopes[scope] = null
  scopeKeys
    .filter((s) => s.key !== null)
    .forEach((s, i) => {
      scopes[s.scope] = decodeUint64(second[i])
    })
  const paused = {} as Record<PauseFeature, boolean | null>
  PAUSE_FEATURES.forEach((feature, i) => {
    paused[feature] = decodeBool(second[idCalls.length + i])
  })

  const senderId = scopes.sender
  const third: Array<{ target: string; allowFailure: true; callData: string }> = []
  if (senderId !== null && senderId > 0n) {
    third.push({
      target: POLICY_REGISTRY,
      allowFailure: true as const,
      callData: registry.encodeFunctionData('policyAdmin', [senderId])
    })
  }
  const accountScopes: PolicyScopeName[] = []
  if (account) {
    for (const scope of SCOPE_ORDER) {
      const id = scopes[scope]
      if (id === null || id === 0n) continue
      accountScopes.push(scope)
      third.push({
        target: POLICY_REGISTRY,
        allowFailure: true as const,
        callData: registry.encodeFunctionData('isAuthorized', [id, account])
      })
    }
  }
  const thirdRows = third.length > 0 ? await call(third) : []
  const adminOffset = senderId !== null && senderId > 0n ? 1 : 0
  const admin = adminOffset ? decodeAddress(thirdRows[0]) : null
  const accountState: Partial<Record<PolicyScopeName, boolean>> = {}
  accountScopes.forEach((scope, i) => {
    const value = decodeBool(thirdRows[adminOffset + i])
    if (value !== null) accountState[scope] = value
  })

  return { scopes, admin, paused, account: accountState }
}

/**
 * Whether every scope points at the same policy — which is what "one list
 * governs this token" looks like, and is the case on every stock today.
 */
export function sharesOnePolicy(policy: StockPolicy): boolean {
  const ids = SCOPE_ORDER.map((scope) => policy.scopes[scope]).filter((id): id is bigint => id !== null)
  return ids.length > 1 && ids.every((id) => id === ids[0])
}

/** True when a wallet is blocked under any scope that was actually read. */
export function isAccountBlocked(policy: StockPolicy): boolean {
  return Object.values(policy.account).some((allowed) => allowed === false)
}
